import { Middleware, createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';

import {
  ApprovalStatus,
  Lease,
  LeaseInput,
  PaymentStatus,
  SignatureStatus,
} from '../../Graphql/Generated/graphql';
import { CREATE_LEASE, UPDATE_LEASE } from '../../Graphql/mutations';
import { GET_LEASES_BY_OWNER_ID, GET_LEASES_BY_USER_ID } from '../../Graphql/queries';
import amplitudeEvents from '../../Utils/Tracking/amplitude.events';
import { getApiUrl } from '../../Utils/urls';
import { RootState } from '../store';

export interface ILeaseState {
  activeLease: Lease;
  leases: Array<Lease>;
  loading: boolean;
  error: string | null;
}

export interface MutationResponse {
  message: string;
  messageType: 'BLANK' | 'SUCCESS' | 'FAILURE';
}

export interface GetLeasesInput {
  userId: string;
}

export interface GetLeasesByOwnerIdResponse {
  getLeasesByOwnerId: Lease[];
}

export interface GetLeasesByUserIdResponse {
  getLeasesByUserId: Lease[];
}

export interface CreateLeaseInput {
  leaseInfo: LeaseInput;
  propertyId: string;
  userId: string;
}

export interface UpdateLeaseInput {
  leaseInfo: LeaseInput;
  userId: string;
}

export interface CreateLeaseResponse {
  createLease: MutationResponse;
}

export interface UpdateLeaseResponse {
  updateLease: MutationResponse;
}

export const leaseApi = createApi({
  reducerPath: 'leaseApi',
  baseQuery: graphqlRequestBaseQuery({ url: getApiUrl() }),
  endpoints: (builder) => ({
    getLeasesByOwnerId: builder.query<GetLeasesByOwnerIdResponse, GetLeasesInput>({
      query: ({ userId }) => ({
        document: GET_LEASES_BY_OWNER_ID,
        variables: { userId },
      }),
    }),
    getLeasesByUserId: builder.query<GetLeasesByUserIdResponse, GetLeasesInput>({
      query: ({ userId }) => ({
        document: GET_LEASES_BY_USER_ID,
        variables: { userId },
      }),
    }),
    createLease: builder.mutation<CreateLeaseResponse, CreateLeaseInput>({
      query: ({ leaseInfo, propertyId, userId }) => ({
        document: CREATE_LEASE,
        variables: { leaseInfo, propertyId, userId },
      }),
    }),
    updateLease: builder.mutation<UpdateLeaseResponse, UpdateLeaseInput>({
      query: ({ leaseInfo, userId }) => ({
        document: UPDATE_LEASE,
        variables: { leaseInfo, userId },
      }),
    }),
  }),
});

export const leaseSlice = createSlice({
  name: 'lease',
  initialState: {
    activeLease: {},
    leases: [] as Array<Lease>,
    loading: false,
    error: null,
  } as ILeaseState,
  reducers: {
    setActiveLease: (state, action) => {
      state.activeLease = action.payload;
    },
    setLeases: (state, action) => {
      state.leases = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

// amplitude tracking events will be added here (viewLease, updateLease and more)

export const { setActiveLease, setLeases, setLoading, setError } = leaseSlice.actions;

export const selectLeases = (state: RootState) => state.lease.leases;
export const selectActiveLease = (state: RootState) => state.lease.activeLease;

export const {
  useGetLeasesByOwnerIdQuery,
  useLazyGetLeasesByOwnerIdQuery,
  useGetLeasesByUserIdQuery,
  useLazyGetLeasesByUserIdQuery,
  useCreateLeaseMutation,
  useUpdateLeaseMutation,
} = leaseApi;
